import * as Yup from "yup";
import dayjs from "dayjs";

const REGEX_PATTERNS = {
  NAME: /^[a-zA-Z\s'-]+$/,
  PHONE: /^[0-9\s()+-]+$/,
  ADDRESS: /^[a-zA-Z0-9\s,.'&()#\/-]+$/,
  POSTCODE: /^\d{4}$/,
  DP_NUMBER: /^\d{1,}[a-zA-Z]{0,2}$/,
  AGENCY_NAME: /^[a-zA-Z0-9\s&'.-]+$/,
  EXPIRY_MONTH: /^(0[1-9]|1[0-2])$/,
  EXPIRY_YEAR: /^20[2-9][0-9]$/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
};

export const applicationValidationSchema = Yup.object({
  state: Yup.string()
    .required("State is required")
    .oneOf(['NSW', 'QLD'], "Invalid state selection"),
  dpNumber: Yup.string()
    .required("DP/SP/CTS/OC number is required")
    .matches(
      REGEX_PATTERNS.DP_NUMBER,
      "Must be numbers only, optionally followed by a letter and numbers. Do not include prefixes or building names. Valid examples: '12345', '12345M', '12345M1'"
    ),
  lotNumber: Yup.string()
    .required("Lot number is required")
    .matches(REGEX_PATTERNS.ALPHANUMERIC, "Lot number can only contain letters and numbers"),
  // qldBrand: Yup.string().required("QLD brand is required"),
  // nswBrand: Yup.string().required("NSW brand is required"),
  brand: Yup.string().required("Brand is required"),
  firstName: Yup.string()
    .required("First name is required")
    .matches(REGEX_PATTERNS.NAME, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  lastName: Yup.string()
    .required("Last name is required")
    .matches(REGEX_PATTERNS.NAME, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  contactNumber: Yup.string()
    .required("Contact number is required")
    .matches(REGEX_PATTERNS.PHONE, "Contact number can only contain numbers, spaces, parentheses, plus, and hyphen"),
  address: Yup.string()
    .required("Address is required")
    .matches(REGEX_PATTERNS.ADDRESS, "Address should contain only alphanumeric characters, spaces, commas, periods, apostrophes, ampersands, parentheses, hash symbols, hyphens, and forward slashes"),
  suburb: Yup.string()
    .required("Suburb is required")
    .matches(REGEX_PATTERNS.ADDRESS, "Suburb should contain only alphanumeric characters, spaces, commas, periods, apostrophes, ampersands, parentheses, hash symbols, hyphens, and forward slashes"),
  postcode: Yup.string()
    .required("Postcode is required")
    .matches(REGEX_PATTERNS.POSTCODE, "Postcode must be 4 digits"),
  country: Yup.string()
    .required("Country is required")
    .matches(REGEX_PATTERNS.NAME, "Country should contain only letters, spaces, hyphens, and apostrophes"),
  statusToRecords: Yup.string().required(
    "Applicant's status to scheme records is required"
  ),
  // layeredSchemeStatus: Yup.string().required("Layered scheme status is required"),
  //formik.values.statusToRecords === "agent_appointed_by_the_requestor" || formik.values.statusToRecords === "another_person_with_a_proper_interest_in_the_information_sought"
  files: Yup.array().when(["statusToRecords", "state"], {
    is: (statusToRecords, state) =>
      statusToRecords === "agent_appointed_by_the_requestor" ||
      statusToRecords ===
        "another_person_with_a_proper_interest_in_the_information_sought" ||
      statusToRecords === "prospective_lot_buyer",
    then: () =>
      Yup.array()
        .min(1, "File is required")
        .test("fileType", "Only PDF files are allowed", (value) => {
          if (!value) return true;
          return value.every((file) => file.type === "application/pdf");
        })
        .test("fileSize", "Files must be less than 10MB", (value) => {
          if (!value) return true;
          return value.every((file) => file.size <= 10 * 1024 * 1024); // 10MB in bytes
        }),
  }),
  agencyName: Yup.string().when("statusToRecords", {
    is: "agent_appointed_by_the_requestor",
    then: () => Yup.string()
      .required("Agency name is required")
      .matches(REGEX_PATTERNS.AGENCY_NAME, "Agency name can only contain letters, numbers, spaces, ampersand, apostrophes, periods, and hyphens"),
  }),
  readAndAccepted: Yup.boolean()
    .required("You must agree to the terms and conditions")
    .oneOf([true], "You must agree to the terms and conditions"),
  hours: Yup.number()
    .required("Hours are required")
    .min(1, "Hours must be greater than 0")
    .max(4, "Hours must be less than 24"),
  date: Yup.date()
    .required("Date is required")
    .min(dayjs(new Date()), "Date must be in the future"),
});

export const paymentValidationSchema = Yup.object({
  cardNumber: Yup.string()
    .required("Card number is required")
    .transform((value) => value.replace(/[\s-]/g, "")) // Remove spaces and hyphens
    .matches(
      /^(?:4[0-9]{12}(?:[0-9]{3})?|(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11})$/,
      "Invalid card number"
    ),
  cardName: Yup.string().required("Card name is required"),
  expiryMonth: Yup.string()
    .required("Expiry month is required")
    .matches(REGEX_PATTERNS.EXPIRY_MONTH, "Invalid expiry month"),
  expiryYear: Yup.string()
    .required("Expiry year is required")
    .matches(REGEX_PATTERNS.EXPIRY_YEAR, "Expected YYYY format")
    .test("expiry", "Year must not be in the past", (value) => {
      if (!value) return false;
      const currentYear = new Date().getFullYear();
      return parseInt(value) >= currentYear;
    }),
  cvn: Yup.string()
    .required("CVN is required")
    .matches(/^[0-9]{3,4}$/, "Invalid CVN"),
  billingAddress: Yup.string()
    .required("Billing address is required")
    .matches(REGEX_PATTERNS.ADDRESS, "Billing address should contain only alphanumeric characters, spaces, commas, periods, apostrophes, ampersands, parentheses, hash symbols, hyphens, and forward slashes"),
  billingSuburb: Yup.string()
    .required("Billing suburb is required")
    .matches(REGEX_PATTERNS.ADDRESS, "Billing suburb should contain only alphanumeric characters, spaces, commas, periods, apostrophes, ampersands, parentheses, hash symbols, hyphens, and forward slashes"),
  billingPostcode: Yup.string()
    .required("Billing postcode is required")
    .matches(REGEX_PATTERNS.POSTCODE, "Billing postcode must be 4 digits"),
  billingState: Yup.string()
    .required("Billing state is required")
    .oneOf(['NSW', 'QLD', 'TAS', 'VIC', 'SA', 'WA', 'NT', 'ACT'], "Invalid state selection"),
  billingCountry: Yup.string()
    .required("Billing country is required")
    .matches(REGEX_PATTERNS.NAME, "Billing country should contain only letters, spaces, hyphens, and apostrophes"),
});
